import { useState } from "react";
import { FileText, Download, ExternalLink } from "lucide-react";

const KIND_LABEL = {
  ingredients: "Ingredients",
  usage_guide: "Usage guide",
  fact_sheet: "Fact sheet",
  brochure: "Brochure",
  other: "PDF",
};

/**
 * Embedded PDF preview card. We don't always want to render a heavy
 * <object>/<iframe> immediately — the card stays compact until the user
 * clicks "Preview" (one extra tap, much faster initial paint).
 *
 * On click → in-place iframe with the PDF source. Mobile sees a button
 * to "Open in new tab" instead, since iOS Safari struggles to render
 * PDFs inline reliably.
 */
export default function PdfPreviewCard({ pdf }) {
  const [open, setOpen] = useState(false);
  if (!pdf?.url) return null;
  const label = KIND_LABEL[pdf.kind] || "Document";

  return (
    <div
      className="rounded-2xl border border-emerald-100 bg-white overflow-hidden"
      data-testid={`shop-pdf-card-${pdf.kind || "other"}`}
    >
      <div className="flex items-start gap-3 p-4 sm:p-5">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-amber-50 text-amber-700 shrink-0">
          <FileText className="w-5 h-5" strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">{label}</p>
          <h4 className="text-sm font-semibold text-emerald-950 mt-0.5 truncate">{pdf.title || "Download PDF"}</h4>
          {pdf.pages ? (
            <p className="text-[11px] text-emerald-900/55 mt-0.5">{pdf.pages} pages</p>
          ) : null}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full bg-emerald-700 text-white px-3 py-1.5 hover:bg-emerald-800 transition"
              data-testid={`shop-pdf-preview-${pdf.kind || "other"}`}
            >
              {open ? "Hide preview" : "Preview"}
            </button>
            <a
              href={pdf.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full border border-emerald-200 bg-white text-emerald-800 px-3 py-1.5 hover:border-emerald-300 hover:bg-emerald-50 transition"
              data-testid={`shop-pdf-open-${pdf.kind || "other"}`}
            >
              <ExternalLink className="w-3 h-3" /> Open
            </a>
            <a
              href={pdf.url}
              download
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full text-emerald-700 px-3 py-1.5 hover:bg-emerald-50 transition"
              data-testid={`shop-pdf-download-${pdf.kind || "other"}`}
            >
              <Download className="w-3 h-3" /> Download
            </a>
          </div>
        </div>
      </div>
      {open && (
        <div className="border-t border-emerald-100 bg-emerald-50/30 p-2 sm:p-3" data-testid="shop-pdf-iframe-wrapper">
          <iframe
            src={pdf.url}
            title={pdf.title || "PDF preview"}
            className="w-full h-[420px] sm:h-[560px] rounded-xl bg-white"
            loading="lazy"
          />
          <p className="text-[11px] text-emerald-900/55 mt-1.5 text-center sm:hidden">
            Preview can be slower on mobile — use <a href={pdf.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Open</a> for the best experience.
          </p>
        </div>
      )}
    </div>
  );
}
